import cn from "classnames";
import { Button, getButtonClassName } from "web-ui";

import {
  type BaseStatus,
  type Challenge,
  languageChallengeApi,
} from "../api/languageChallenge";
import { type User } from "../api/v2";
import languages from "../data/languages.json";
import styles from "../styles/Profile.module.scss";

import Avatar from "./Avatar";
import BonusChallengeAndroid from "./BonusChallengeAndroid";
import StreakProgress from "./StreakProgress";
import XpProgress from "./XpProgress";

export interface Props {
  className?: string;
  status: BaseStatus;
  user: User;
  allowReset: boolean;
  shownChallenge: Challenge;
}
const Profile = ({
  className,
  status,
  user,
  allowReset,
  shownChallenge,
}: Props) => {
  const challengeName =
    status.learningLanguage === "mega"
      ? "Mega challenge"
      : `${languages[status.learningLanguage].name} from ${
          languages[status.fromLanguage].name
        } learning challenge`;
  const reset = async () => {
    if (
      !confirm(
        `Are you sure you want to reset your own progress in the ${challengeName}? This cannot be undone!`,
      ) ||
      prompt("Please type “DELETE MY PROGRESS” to confirm.") !==
        "DELETE MY PROGRESS"
    ) {
      alert("Reset cancelled.");
      return;
    }

    await languageChallengeApi.deleteRegistration(user.id, shownChallenge.id);
    window.location.reload();
  };
  return (
    <div className={className}>
      <div className={styles.wrap}>
        <div className={styles["avatar-row"]}>
          <Avatar className={styles.avatar} user={user} />
          <div className={styles["name-column"]}>
            <h3 className={styles.name}>{user.name ?? user.username}</h3>
            <p className={styles.email}>{status.email}</p>
          </div>
          {allowReset ? (
            <a className={styles.reset} onClick={reset}>
              reset
            </a>
          ) : null}
          <h2 className={styles["xp-text"]}>
            {status.xpObjective.progress} XP
          </h2>
        </div>
        <XpProgress className={styles["xp-progress"]} status={status} />
        {status.streakObjective ? (
          <StreakProgress
            className={styles["streak-progress"]}
            objective={status.streakObjective}
          />
        ) : null}
        {status.surveyLink &&
          (status.hasCompletedSurvey ? (
            <div className={styles["survey-row"]}>
              <Button
                className={styles["survey-button"]}
                color="macaw"
                disabled={true}
                useDisabledAttribute={true}
                variant="solid"
              >
                survey completed
              </Button>
              <p className={styles["survey-text"]}>
                You finished the challenge! 🎉
              </p>
            </div>
          ) : (
            (() => {
              const disabled =
                status.xpObjective.progress < status.xpObjective.goal;
              return (
                <div className={styles["survey-row"]}>
                  <a
                    className={cn(
                      getButtonClassName({
                        color: "macaw",
                        fakeDisabled: disabled,
                        variant: "solid",
                      }),
                      styles["survey-button"],
                      { [styles["survey-button-disabled"]]: disabled },
                    )}
                    href={status.surveyLink}
                  >
                    start survey
                  </a>
                  <div className={styles["survey-text"]}>
                    Complete this survey once you earn{" "}
                    <span className={styles["survey-text-xp-span"]}>
                      {status.xpObjective.goal} XP
                    </span>{" "}
                    {status.streakObjective ? (
                      <span>
                        and a{" "}
                        <span className={styles["survey-text-streak-span"]}>
                          {status.streakObjective.goal} day longest streak
                          during the challenge
                        </span>{" "}
                      </span>
                    ) : null}
                    to complete the challenge!
                  </div>
                </div>
              );
            })()
          ))}
      </div>
      <div className={styles.wrap}>
        <BonusChallengeAndroid
          androidObjective={status.androidXpObjective}
          userId={user.id}
        />
      </div>
    </div>
  );
};
export default Profile;
